"use client";

import styles from "./VerificationPhoneOptionalStep.module.scss";
import {
  useEffect,
  useRef,
  useState } from "react";
import { useTranslations } from "@/i18n";
import {
  PhoneNumberInput,
  ResendPhoneVerificationCode,
} from "@/components";

import { useAuth, useAuthStore, useForm } from "@/hooks";
import { setCurrentStep, setFieldError, setFieldValue } from "@/stores";
import {
  sendPhoneCode,
  checkPhoneAvailability,
} from "@/pages/sign-up/_helpers";
import { verifyAccountPhone } from "@/helpers";
import {
  Button,
  Flex,
  Fieldset,
  InputOtp,
} from "@vetty/ui";

export const VerificationPhoneOptionalStep = () => {
  const t = useTranslations("verification.phoneOptional");
  const { user } = useAuth();
  const { refreshAuth } = useAuthStore();
  const { values, errors } = useForm();
  const [verificationCode, setVerificationCode] = useState("");
  const [otpKey, setOtpKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [fullPhone, setFullPhone] = useState("");
  const verifyInFlight = useRef(false);

  const phoneVerified = Boolean(user?.phoneVerified);

  useEffect(() => {
    if (!values.phonePrefix) {
      setFieldValue("phonePrefix", "+48");
    }
  }, [values.phonePrefix]);

  useEffect(() => {
    if (phoneVerified) {
      setCurrentStep(3);
    }
  }, [phoneVerified]);

  const handleSkip = () => {
    setCurrentStep(3);
  };

  const handleSendCode = async () => {
    const phonePrefix = values.phonePrefix || "+48";
    if (!values.phonePrefix) setFieldValue("phonePrefix", phonePrefix);

    const rawNumber = String(values.phoneNumber ?? "").replaceAll(" ", "");
    if (!rawNumber) {
      setFieldError("phoneNumber", t("noPhoneNumber"));
      return;
    }

    const phone = `${phonePrefix}${rawNumber}`;

    const isAvailable = await checkPhoneAvailability(phone);
    if (!isAvailable) {
      setFieldError("phoneNumber", t("phoneInUse"));
      return;
    }

    setIsSendingCode(true);
    const sent = await sendPhoneCode(phone);
    setIsSendingCode(false);

    if (sent) {
      setFullPhone(phone);
      setCodeSent(true);
      setFieldValue("phoneNumber", values.phoneNumber || "");
      setFieldValue("phonePrefix", phonePrefix);
    } else {
      setFieldError("phoneNumber", t("sendCodeFailed"));
    }
  };

  const handleVerify = async (codeParam?: string) => {
    if (verifyInFlight.current) return;
    const code = (codeParam ?? verificationCode).replace(/\D/g, "").slice(0, 6);

    if (!code || code.length < 6) {
      setFieldError("phoneVerificationCode", t("invalidCode"));
      return;
    }

    const phoneToVerify =
      fullPhone ||
      `${values.phonePrefix || "+48"}${String(values.phoneNumber ?? "").replaceAll(
        " ",
        ""
      )}`;

    verifyInFlight.current = true;
    setIsLoading(true);
    setFieldError("phoneVerificationCode", "");
    try {
      const ok = await verifyAccountPhone(phoneToVerify, code);

      if (ok) {
        await refreshAuth();
        setCurrentStep(3);
      } else {
        setFieldError("phoneVerificationCode", t("verificationFailed"));
        setOtpKey((k) => k + 1);
        setVerificationCode("");
      }
    } finally {
      setIsLoading(false);
      verifyInFlight.current = false;
    }
  };

  return (
    <Flex direction={"column"} className={styles["comp"]}>
      <Flex className={styles["comp__signing-as"]} gap={5}>
        <div>{t("signingAsLabel")}</div>
        <strong>{user?.email ?? ""}</strong>
      </Flex>

      <p className={styles["comp__sub-note"]}>
        {codeSent ? (
          <>
            {t("codeSentHint")} <strong>{fullPhone}</strong>
          </>
        ) : (
          t("noteHint")
        )}
      </p>

      <Flex direction={"column"} gap={24}>
        {!codeSent && (
          <>
            <Fieldset
              isError={!!errors.phoneNumber}
              errorMessage={errors.phoneNumber}
            >
              <Flex direction={"column"} gap={8}>
                <PhoneNumberInput
                  id="verificationPhoneNumber"
                  defaultValue={String(values.phoneNumber ?? "")}
                  onChange={(v) => setFieldValue("phoneNumber", v)}
                />
              </Flex>
            </Fieldset>

            <Flex gap={12} className={styles["comp__actions"]}>
              <Button
                variant="secondary"
                onClick={handleSkip}
                className={styles["comp__submit-button"]}
              >
                {t("skip")}
              </Button>
              <Button
                onClick={handleSendCode}
                loading={isSendingCode}
                disabled={isSendingCode}
                className={styles["comp__submit-button"]}
              >
                {t("sendCode")}
              </Button>
            </Flex>
          </>
        )}

        {codeSent && (
          <>
            <Fieldset
              isError={!!errors.phoneVerificationCode}
              errorMessage={errors.phoneVerificationCode}
            >
              <Flex direction={"column"} gap={8}>
                <InputOtp
                  key={otpKey}
                  disabled={isLoading}
                  label={t("codeLabel")}
                  hasError={!!errors.phoneVerificationCode}
                  onChange={(v) => {
                    setVerificationCode(v);
                    setFieldError("phoneVerificationCode", "");
                  }}
                  onSubmit={(full) => {
                    void handleVerify(full);
                  }}
                />
              </Flex>
            </Fieldset>

            <Flex gap={12} className={styles["comp__actions"]}>
              <Button
                variant="secondary"
                onClick={handleSkip}
                className={styles["comp__submit-button"]}
              >
                {t("skip")}
              </Button>
              <Button
                loading={isLoading}
                disabled={isLoading || verificationCode.length !== 6}
                onClick={() => void handleVerify()}
                className={styles["comp__submit-button"]}
              >
                {t("verify")}
              </Button>
            </Flex>

            <div className={styles["comp__resend"]}>
              <ResendPhoneVerificationCode />
            </div>
          </>
        )}
      </Flex>
    </Flex>
  );
};
